// react
import type { Control } from 'react-hook-form';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Stack } from '@mui/material';

// shared components
import { RHFSelect, RHFTextField } from '@/components/ui/form';

// feature-components
import { SeverityBars } from '@/features/alarm-hub/components/SeverityBars';

// relative
import {
  type AlarmConfigurationFormValues,
  SEVERITY_LEVELS,
} from './alarmConfigurationForm.schema';

type AlarmConfigurationDetailsFieldsProps = {
  control: Control<AlarmConfigurationFormValues>;
};

/**
 * Shared "Details" fields (Title/Message/Sub Type/Severity) reused by every
 * alarm configuration create/edit form, regardless of alarm type.
 */
export function AlarmConfigurationDetailsFields({
  control,
}: AlarmConfigurationDetailsFieldsProps) {
  const { t } = useTranslation('forms');

  return (
    <Stack spacing={2}>
      <RHFTextField
        name="title"
        control={control}
        label={t('alarmConfigurationForm.title.label')}
        placeholder={t('alarmConfigurationForm.title.placeholder')}
        helperText={t('alarmConfigurationForm.title.helperText')}
        fullWidth
      />

      <RHFTextField
        name="message"
        control={control}
        label={t('alarmConfigurationForm.message.label')}
        placeholder={t('alarmConfigurationForm.message.placeholder')}
        helperText={t('alarmConfigurationForm.message.helperText')}
        fullWidth
        multiline
        minRows={2}
      />

      <RHFTextField
        name="alarmSubtype"
        control={control}
        required
        label={t('alarmConfigurationForm.alarmSubtype.label')}
        placeholder={t('alarmConfigurationForm.alarmSubtype.placeholder')}
        helperText={t('alarmConfigurationForm.alarmSubtype.helperText')}
        fullWidth
      />

      <RHFSelect
        name="severity"
        control={control}
        label={t('alarmConfigurationForm.severity.label')}
        helperText={t('alarmConfigurationForm.severity.helperText')}
        fullWidth
        options={SEVERITY_LEVELS.map((level) => ({
          value: level,
          label: t(
            `alarmConfigurationForm.severity.options.${level.toLowerCase()}`,
            {
              defaultValue: level,
            },
          ),
          icon: <SeverityBars severity={level} label="" showLabel={false} />,
        }))}
      />
    </Stack>
  );
}
